"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { postJson } from "@lib/http";
import { useAgentOverlayContext } from "./AgentOverlayProvider";
import type { OverlayMessage } from "./types";
import type { PersistedTutorMessage, TutorSessionSummary } from "@lib/agent/tutorChat/types";

/**
 * A turn is worth saving if it says something or proposes something: an
 * action-only turn is empty text, and the empty-text filter used to drop it,
 * losing the action and its outcome on reload (CON-123).
 */
function toPersisted(messages: OverlayMessage[]): PersistedTutorMessage[] {
  return messages
    .filter(
      (m) =>
        (m.role === "user" || m.role === "tutor") && !m.isStreaming && (m.text.trim() || m.action),
    )
    .map((m) => ({
      id: m.id,
      role: m.role as "user" | "tutor",
      text: m.text,
      ...(m.action ? { action: m.action } : {}),
      ...(m.actionStatus ? { actionStatus: m.actionStatus } : {}),
    }));
}

function toOverlay(messages: PersistedTutorMessage[]): OverlayMessage[] {
  return messages.map((m) => ({
    id: m.id,
    role: m.role,
    text: m.text,
    isStreaming: false,
    ...(m.action ? { action: m.action } : {}),
    ...(m.actionStatus ? { actionStatus: m.actionStatus } : {}),
  }));
}

export type UseTutorSessionsReturn = {
  sessions: TutorSessionSummary[];
  activeSessionId: number | null;
  loading: boolean;
  newSession: () => void;
  switchSession: (id: number) => Promise<void>;
  refresh: () => Promise<void>;
};

export function useTutorSessions(): UseTutorSessionsReturn {
  const { state, loadTutorMessages } = useAgentOverlayContext();
  const { messages, mode, isOpen } = state;
  const isTutor = mode.type === "tutor";
  const isStreaming = messages.some((m) => m.isStreaming);

  const [sessions, setSessions] = useState<TutorSessionSummary[]>([]);
  const [activeSessionId, setActiveSessionId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);

  // Mirrors activeSessionId for the persist effect below, which intentionally
  // does not depend on activeSessionId (it would otherwise re-fire on every
  // session switch). Only ever written outside of render: here in effects and
  // callbacks, never during the render body itself.
  const activeRef = useRef<number | null>(null);
  const lastSavedRef = useRef<string>("");
  const loadedRef = useRef(false);

  const refresh = useCallback(async () => {
    try {
      const res = await fetch("/api/tutor-chat/sessions");
      if (!res.ok) return;
      const data = (await res.json()) as { sessions: TutorSessionSummary[] };
      setSessions(data.sessions);
    } catch {
      /* quiet: the live chat keeps working */
    }
  }, []);

  // Hydrate the newest session the first time the widget opens in tutor mode.
  useEffect(() => {
    if (!isOpen || !isTutor || loadedRef.current) return;
    loadedRef.current = true;
    setLoading(true);
    void (async () => {
      try {
        const res = await fetch("/api/tutor-chat/sessions");
        if (!res.ok) return;
        const data = (await res.json()) as { sessions: TutorSessionSummary[] };
        setSessions(data.sessions);
        const newest = data.sessions[0];
        if (!newest) return;
        const detailRes = await fetch(`/api/tutor-chat/sessions/${newest.id}`);
        if (!detailRes.ok) return;
        const detail = (await detailRes.json()) as { messages: PersistedTutorMessage[] };
        setActiveSessionId(newest.id);
        activeRef.current = newest.id;
        lastSavedRef.current = JSON.stringify(detail.messages);
        loadTutorMessages(toOverlay(detail.messages));
      } catch {
        /* quiet: the live chat keeps working */
      } finally {
        setLoading(false);
      }
    })();
  }, [isOpen, isTutor, loadTutorMessages]);

  // Persist once per settled exchange (no message streaming).
  useEffect(() => {
    if (!isTutor || isStreaming) return;
    const persist = toPersisted(messages);
    if (persist.length === 0) return;
    const signature = JSON.stringify(persist);
    if (signature === lastSavedRef.current) return;
    lastSavedRef.current = signature;
    void (async () => {
      try {
        let id = activeRef.current;
        if (id == null) {
          const res = await postJson("/api/tutor-chat/sessions", {});
          if (!res.ok) {
            lastSavedRef.current = "";
            return;
          }
          const created = (await res.json()) as { id: number };
          id = created.id;
          setActiveSessionId(id);
          activeRef.current = id;
        }
        const saveRes = await fetch(`/api/tutor-chat/sessions/${id}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ messages: persist }),
        });
        if (!saveRes.ok) {
          lastSavedRef.current = "";
          return;
        }
        void refresh();
      } catch {
        lastSavedRef.current = "";
      }
    })();
  }, [messages, isTutor, isStreaming, refresh]);

  const newSession = useCallback(() => {
    setActiveSessionId(null);
    activeRef.current = null;
    lastSavedRef.current = "";
    loadTutorMessages([]);
  }, [loadTutorMessages]);

  const switchSession = useCallback(
    async (id: number) => {
      const res = await fetch(`/api/tutor-chat/sessions/${id}`);
      if (res.status === 404) {
        void refresh();
        return;
      }
      if (!res.ok) return;
      const detail = (await res.json()) as { messages: PersistedTutorMessage[] };
      setActiveSessionId(id);
      activeRef.current = id;
      lastSavedRef.current = JSON.stringify(detail.messages);
      loadTutorMessages(toOverlay(detail.messages));
    },
    [loadTutorMessages, refresh],
  );

  return { sessions, activeSessionId, loading, newSession, switchSession, refresh };
}
